import './underwriting.policy.page.scss';
import {
  BindPageSubjectivity,
  DocumentType,
  FirstPaginationNode,
  GetBindPageQuery,
  NoteTargetAreaType,
  RenewalInstructionType,
  SubjectivityDeadline,
  SubmissionStatus,
  useGetNotes,
  useGetPolicyPageQuery,
  useUpsertSubmissionMutation,
  useUnderwritingEvaluationMutation,
  useUpsertPolicyMutation,
} from '@converge/graphql';
import { Box, IconButton, Stack } from '@mui/material';
import {
  ConfirmDialog,
  ConvergePdfViewer,
  ConvergeSectionTitle,
  NewConvergeButton,
  NewFromSelectField,
  PaperLayout,
} from '../../../presentation';
import { FormProvider, useForm } from 'react-hook-form';
import {
  SpeakerNotes,
  SpeakerNotesOff,
  SpeakerNotesOutlined,
} from '@mui/icons-material';
import { useCallback, useRef, useState } from 'react';

import { ConvergeNotes } from '../../notes';
import { DocumentButton } from '../submission-action/document-button';
import { ISubmissionProps } from '../overview';
import { SurplusLineHandle } from '../overview/underwriting.submission.surplusLine';
import { UnderwritingBindOrPolicyHeader } from '../underwriting.bind-or-policy.header';
import { UnmetSubjectivitiesDialog } from '../underwriting.unmet-subjectivities-dialog';
import { UnderwritingSubjectivityList } from '../subjectivities';
import { UnderwritingSubmissionDetailPageSlug } from '@converge/data';
import { UnderwritingSubmissionPageContainer } from '../underwriting.submission.page.container';
import { convergeLogger } from '@converge/config';
import { isApolloError } from '@apollo/client';
import { useConvergeSnackBar } from '../../../contexts';
import { useOutletContext } from 'react-router-dom';
import { useUnderwritingSubmissionDetailPage } from '../underwriting.submission.detail.hooks';
import { useUnderwritingSubmissionStateContext } from '../underwriting.submission.context';

const renewalInstructionOptions = [
  {
    label: 'Conditional renewal',
    value: RenewalInstructionType.ConditionalRenewal,
  },
  {
    label: 'Non renewal',
    value: RenewalInstructionType.NonRenewal,
  },
  {
    label: 'Renewal',
    value: RenewalInstructionType.Renewal,
  },
];
export const UnderwritingPolicyPage = () => {
  const { navigate } = useUnderwritingSubmissionDetailPage();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isNotesOpen, setIsNotesOpen] = useState(false);
  const [
    showHasUnmetSubjectivitiesDialog,
    setShowHasUnmetSubjectivitiesDialog,
  ] = useState(false);
  const [unmetSubjectivities, setUnmetSubjectivities] = useState<string[]>([]);
  const [isSubjectivitiesLoading, setIsSubjectivitiesLoading] = useState(false);
  const [isFetch, setIsFetch] = useState(false);
  const surplusLineRef = useRef<SurplusLineHandle>(null);
  const {
    submission: { uuid: submissionUuid, status, renewal },
    info: { boundQuote },
  } = useUnderwritingSubmissionStateContext();
  const { submission: submissionInfo } = useOutletContext<ISubmissionProps>();
  const { upsertSubmission, loading: loadingUpsert } =
    useUpsertSubmissionMutation();

  const { hasNotes } = useGetNotes({
    targetUuid: boundQuote?.uuid,
    targetArea: NoteTargetAreaType.Policy,
  });
  const {
    info: { quote, subjectivities },
  } = useGetPolicyPageQuery({
    submissionUuid,
    quoteUuid: boundQuote?.uuid ?? '',
  });

  const currentPolicy = quote?.policies?.edges[0].node;

  const validateBindWarning = useCallback(() => {
    const subjectivitiesToBeWaivedOrSatisfied = subjectivities
      ?.filter(
        (s) =>
          s.quote?.deadline === SubjectivityDeadline.PriorToPolicy &&
          !s.quote?.waived &&
          !s.quote?.isSatisfied
      )
      .map((s) => {
        return s.name ?? '';
      });

    if (subjectivitiesToBeWaivedOrSatisfied.length > 0) {
      setShowConfirmDialog(false);
      setShowHasUnmetSubjectivitiesDialog(true);
      setUnmetSubjectivities(subjectivitiesToBeWaivedOrSatisfied);
    } else {
      setShowConfirmDialog(true);
      setShowHasUnmetSubjectivitiesDialog(false);
    }
  }, [subjectivities]);

  const handleConfirm = async () => {
    try {
      setShowConfirmDialog(false);
      await upsertSubmission({
        variables: {
          details: {
            uuid: submissionUuid,
            status:
              status === SubmissionStatus.Bound
                ? SubmissionStatus.PolicyIssued
                : SubmissionStatus.Bound,
            subStatus: null,
          },
        },
      });
      notify({
        message:
          status !== SubmissionStatus.PolicyIssued
            ? 'Policy issued successfully'
            : 'Policy issuance undone successfully',
        variant: 'success',
      });
    } catch (error) {
      convergeLogger.error(error);
      notify(error instanceof Error ? error.message : `${error}`, {
        variant: 'error',
      });
    }
  };

  const policySubjectivities = subjectivities.filter(
    (subject) => subject?.quote?.deadline !== SubjectivityDeadline.PriorToBind
  );
  console.log('arash', surplusLineRef);

  const formMethods = useForm({});
  const { upsertPolicy } = useUpsertPolicyMutation();

  const {
    createUnderwritingEvaluation,
    loading: underwritingEvaluationLoading,
  } = useUnderwritingEvaluationMutation();
  const { notify } = useConvergeSnackBar();
  const UnderwritingEvaluationHandler = async (submissionUuid: string) => {
    try {
      await createUnderwritingEvaluation({
        variables: {
          submissionUuid,
        },
      });

      notify({
        message: 'Underwriting evaluation created successfully',
        variant: 'success',
      });
    } catch (error) {
      convergeLogger.error(error);
      notify(`Failed to generate underwriting evaluation`, {
        variant: 'error',
      });
    }
  };

  return (
    <UnderwritingSubmissionPageContainer
      title="Policy"
      footerLeft={
        <Stack className="policy-footer">
          <Box className="button-box first-button-box">
            <NewConvergeButton
              disabled={
                (!!status &&
                  ![
                    SubmissionStatus.Bound,
                    SubmissionStatus.PolicyIssued,
                  ].includes(status)) ||
                isSubjectivitiesLoading
              }
              loading={loadingUpsert}
              label={
                status === SubmissionStatus.Bound
                  ? 'Issue policy'
                  : status === SubmissionStatus.PolicyIssued
                  ? 'Undo policy issuance'
                  : `No actions to take because submission is ${status}`
              }
              onClick={() => validateBindWarning()}
            />
          </Box>
          <Box className="button-box">
            <NewConvergeButton
              onClick={() => {
                UnderwritingEvaluationHandler(submissionUuid);
              }}
              disabled={
                (!!status &&
                  ![
                    SubmissionStatus.Bound,
                    SubmissionStatus.PolicyIssued,
                  ].includes(status)) ||
                isSubjectivitiesLoading
              }
              loading={underwritingEvaluationLoading}
              label={'Create uw evaluation'}
              startIcon={''}
            />
          </Box>
          {submissionInfo?.documentLink && (
            <Box className="button-box">
              <DocumentButton
                submissionDocumentLink={submissionInfo.documentLink}
              />
            </Box>
          )}
          <ConfirmDialog
            show={showConfirmDialog}
            title={`${
              status === SubmissionStatus.Bound
                ? 'Issue policy'
                : 'Undo policy issuance'
            }`}
            confirmButtonText="Yes"
            cancelButtonText="No"
            content={
              status === SubmissionStatus.Bound
                ? 'Are you sure you want to issue this policy?'
                : 'Are you sure you want to undo the policy issuance?'
            }
            onCancel={() => setShowConfirmDialog(false)}
            onConfirm={handleConfirm}
          />
          <UnmetSubjectivitiesDialog
            open={showHasUnmetSubjectivitiesDialog}
            onClose={() => setShowHasUnmetSubjectivitiesDialog(false)}
            subjectivities={unmetSubjectivities}
          />
        </Stack>
      }
      onPrevious={() => {
        navigate.page({
          sectionUrl: UnderwritingSubmissionDetailPageSlug.Bind,
        });
      }}
    >
      <PaperLayout className="policy-container">
        <Stack>
          <UnderwritingBindOrPolicyHeader
            quote={quote as FirstPaginationNode<GetBindPageQuery['quotes']>}
          />

          <UnderwritingSubjectivityList
            subjectivities={policySubjectivities as BindPageSubjectivity[]}
            submissionUuid={submissionUuid}
            quoteUuid={quote?.uuid ?? ''}
            setIsLoading={setIsSubjectivitiesLoading}
            isLoading={isSubjectivitiesLoading}
            deadline={SubjectivityDeadline.PriorToPolicy}
            isFetch={isFetch}
            setIsFetch={setIsFetch}
            surplusLineRef={surplusLineRef}
            surplusLineFormEnabled
          />
          <ConvergePdfViewer.Stack>
            <ConvergePdfViewer.DownloadedFile
              name="Invoice"
              type={DocumentType.Invoice}
              submissionUuid={submissionUuid}
            />
            <ConvergePdfViewer.DownloadedFile
              name="Policy"
              type={DocumentType.PolicyDocument}
              submissionUuid={submissionUuid}
            />
          </ConvergePdfViewer.Stack>
          <br />
          <Stack flexDirection={'row'} gap={2}>
            <FormProvider {...formMethods}>
              <Box className="renew-policy-section">
                <ConvergeSectionTitle>
                  Renewal Instructions
                </ConvergeSectionTitle>
                <Box sx={{ display: 'flex' }}>
                  <NewFromSelectField
                    disabled={renewal === true}
                    name=""
                    data-testid="renewal-select"
                    options={renewalInstructionOptions}
                    value={currentPolicy?.renewalInstructionType}
                    onChange={async (event) => {
                      setIsNotesOpen(true);
                      const renewalInstructionType = event?.target
                        ?.value as RenewalInstructionType;
                      try {
                        notify({
                          message: `Updating renewal instructions...`,
                          variant: 'info',
                        });
                        await upsertPolicy({
                          variables: {
                            details: {
                              uuid: currentPolicy?.uuid || '',
                              renewalInstructionType: renewalInstructionType,
                            },
                          },
                        });
                        notify({
                          message: 'Renewal policy updated successfully',
                          variant: 'success',
                        });
                      } catch (cause) {
                        notify({
                          message: `Failed to update renewal type ${
                            cause instanceof Error && isApolloError(cause)
                              ? cause.message
                              : cause
                          }`,
                          variant: 'error',
                        });
                      }
                    }}
                  />
                  <IconButton
                    disabled={false}
                    onClick={() => {
                      setIsNotesOpen(!isNotesOpen);
                    }}
                    className="policy-note-icon-button"
                  >
                    {hasNotes && !isNotesOpen ? (
                      <SpeakerNotes />
                    ) : isNotesOpen ? (
                      <SpeakerNotesOff />
                    ) : (
                      <SpeakerNotesOutlined className="subjectivities-notes-icon" />
                    )}
                  </IconButton>
                </Box>
              </Box>
            </FormProvider>
            <br />
          </Stack>
          <Box>
            {isNotesOpen ? (
              <Stack>
                <ConvergeNotes
                  title="Renewal instruction notes"
                  hideHeader
                  targetArea={NoteTargetAreaType.Policy}
                  targetUuid={boundQuote?.uuid as string}
                  disabled={renewal === true}
                />
              </Stack>
            ) : null}
          </Box>
        </Stack>
      </PaperLayout>
    </UnderwritingSubmissionPageContainer>
  );
};

export default UnderwritingPolicyPage;
